import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, X, Clock, Copy, Check, ExternalLink, CreditCard, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Virtual Account Banks with logos
const vaBanks = [
  { code: 'BRI', name: 'Bank BRI', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/68/BANK_BRI_logo.svg/200px-BANK_BRI_logo.svg.png', type: 'va' },
  { code: 'BNI', name: 'Bank BNI', logo: 'https://customer-assets.emergentagent.com/job_xendit-fva/artifacts/yhkt9ybo_BNI%20LOGO.png', type: 'va' },
  { code: 'MANDIRI', name: 'Bank Mandiri', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/ad/Bank_Mandiri_logo_2016.svg/200px-Bank_Mandiri_logo_2016.svg.png', type: 'va' },
  { code: 'PERMATA', name: 'PermataBank', logo: 'https://customer-assets.emergentagent.com/job_xendit-fva/artifacts/kgb1rd7o_PERMATA%20LOGO.jpg', type: 'va' },
  { code: 'BCA', name: 'Bank BCA', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Bank_Central_Asia.svg/200px-Bank_Central_Asia.svg.png', type: 'va' },
  { code: 'CIMB', name: 'CIMB Niaga', logo: 'https://customer-assets.emergentagent.com/job_xendit-fva/artifacts/kczcbhvi_NIAGA%20LOGO.png', type: 'va' },
  { code: 'BSI', name: 'Bank Syariah', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Bank_Syariah_Indonesia.svg/200px-Bank_Syariah_Indonesia.svg.png', type: 'va' },
];

// Other Payment Methods (QRIS & Alfamart only)
const otherMethods = [
  { code: 'QRIS', name: 'QRIS', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/QRIS_logo.svg/200px-QRIS_logo.svg.png', type: 'qris' },
  { code: 'ALFAMART', name: 'Alfamart', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/ALFAMART_LOGO_BARU.png/200px-ALFAMART_LOGO_BARU.png', type: 'retail' },
];

const PaymentConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = searchParams.get('invoice_id');
  const paymentId = searchParams.get('payment_id');
  const redirectUrl = searchParams.get('redirect_url');
  const selectMode = searchParams.get('select'); // If 'true', always show method selection
  
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Payment method selection state
  const [selectedGateway, setSelectedGateway] = useState('xendit');
  const [selectedMethod, setSelectedMethod] = useState('BRI');
  const [selectedType, setSelectedType] = useState('va'); // 'va', 'qris', 'retail'
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [paymentCreated, setPaymentCreated] = useState(false);
  
  // Force show method selection - initialize based on URL param
  const [showMethodSelection, setShowMethodSelection] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('select') === 'true';
  });

  // // TRIPAY_CUSTOMER_FLOW_V1 — gateway-aware redirect. Fetch details first to know active_gateway,
  // then route to /payment/duitku or /payment/tripay accordingly.
  useEffect(() => {
    fetchPaymentDetails();
  }, [invoiceId, paymentId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customer_token') || localStorage.getItem('token');

      const response = await axios.get(
        `${API_URL}/api/v2/payments/details/${invoiceId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setPaymentData(response.data.data);

        // Auto-redirect to gateway-specific picker page when select=true
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get('select') === 'true' && invoiceId) {
            const gw = (response.data.data?.active_gateway || 'duitku').toLowerCase();
            const target = gw === 'tripay' ? 'tripay' : 'duitku';
            navigate(`/payment/${target}?invoice_id=${invoiceId}`, { replace: true });
          }
        } catch (e) {
          // fall through: stay on confirmation page
        }
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Gagal memuat detail pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPaymentDetails();
    setRefreshing(false);
    toast.success('Data diperbarui');
  };

  
  // // TRIPAY_CUSTOMER_FLOW_V1 — Payment Handler routes to correct gateway page
  const handleDuitkuPayment = async () => {
    if (!invoiceId) return;
    const gw = (paymentData?.active_gateway || 'duitku').toLowerCase();
    const target = gw === 'tripay' ? 'tripay' : 'duitku';
    navigate(`/payment/${target}?invoice_id=${invoiceId}`);
    return;
    // Legacy code below (unreachable) — retained for reference only
    // eslint-disable-next-line no-unreachable
    setCreatingPayment(true);
    try {
      const token = localStorage.getItem('customer_token') || localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/v2/payments/duitku/create`,
        { invoice_id: parseInt(invoiceId) },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success && response.data.data?.payment_url) {
        toast.success('Mengalihkan ke halaman pembayaran Duitku...');
        window.location.href = response.data.data.payment_url;
      } else {
        toast.error(response.data.message || 'Gagal membuat pembayaran');
      }
    } catch (error) {
      console.error('Error creating Duitku payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat pembayaran Duitku');
    } finally {
      setCreatingPayment(false);
    }
  };

  const handleCreatePayment = async () => {
    if (!invoiceId) return;
    
    setCreatingPayment(true);
    try {
      const token = localStorage.getItem('customer_token') || localStorage.getItem('token');
      
      let endpoint = '';
      let successMessage = '';
      
      if (selectedType === 'va') {
        // Create Fixed Virtual Account
        endpoint = `${API_URL}/api/payments/xendit/fixed-va/create?invoice_id=${invoiceId}&bank_code=${selectedMethod}`;
        successMessage = 'Virtual Account berhasil dibuat!';
      } else {
        // For QRIS, Retail (Alfamart/Indomaret) - use Xendit Invoice API with specific payment method
        endpoint = `${API_URL}/api/payments/xendit/create?invoice_id=${invoiceId}&payment_method=${selectedMethod}`;
        successMessage = `Kode pembayaran ${selectedMethod} berhasil dibuat!`;
      }
      
      const response = await axios.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(successMessage);
        setPaymentCreated(true);
        setShowMethodSelection(false); // Hide method selection after success
        
        // For QRIS/Retail, redirect to Xendit payment page
        if (selectedType !== 'va' && response.data.data?.payment_url) {
          window.location.href = response.data.data.payment_url;
          return;
        }
        
        // Refresh payment details to show new VA
        await fetchPaymentDetails();
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat pembayaran');
    } finally {
      setCreatingPayment(false);
    }
  };
  
  const handleSelectMethod = (method, type) => {
    setSelectedMethod(method);
    setSelectedType(type);
  };

  // One-click payment: Directly creates payment when a method is clicked
  const handleDirectPayment = async (method, type) => {
    if (!invoiceId) return;
    
    // Update selected method for UI feedback
    setSelectedMethod(method);
    setSelectedType(type);
    setCreatingPayment(true);
    
    try {
      const token = localStorage.getItem('customer_token') || localStorage.getItem('token');
      
      let endpoint = '';
      let successMessage = '';
      
      if (type === 'va') {
        // Create Fixed Virtual Account
        endpoint = `${API_URL}/api/payments/xendit/fixed-va/create?invoice_id=${invoiceId}&bank_code=${method}`;
        successMessage = 'Virtual Account berhasil dibuat!';
      } else {
        // For QRIS, Retail (Alfamart) - use Xendit Invoice API with specific payment method
        endpoint = `${API_URL}/api/payments/xendit/create?invoice_id=${invoiceId}&payment_method=${method}`;
        successMessage = `Kode pembayaran ${method} berhasil dibuat!`;
      }
      
      const response = await axios.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(successMessage);
        setPaymentCreated(true);
        setShowMethodSelection(false); // Hide method selection after success
        
        // For QRIS/Retail, redirect to Xendit payment page
        if (type !== 'va' && response.data.data?.payment_url) {
          window.location.href = response.data.data.payment_url;
          return;
        }
        
        // Refresh payment details to show new VA
        await fetchPaymentDetails();
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat pembayaran');
    } finally {
      setCreatingPayment(false);
    }
  };

  const handleClose = () => {
    const customerToken = localStorage.getItem('customer_token');
    if (customerToken) {
      // Get tenant code from URL or storage
      const tenantCode = localStorage.getItem('tenant_code') || 'T06122503';
      navigate(`/p/${tenantCode}/dashboard`);
    } else {
      navigate('/app/invoices');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Nomor VA disalin!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID').format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' / ' + date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getPaymentMethodLogo = (method) => {
    const logos = {
      // Xendit / friendly names
      'MANDIRI': '/bank-logos/mandiri.png',
      'BCA': '/bank-logos/bca.png',
      'BNI': '/bank-logos/bni.png',
      'BRI': '/bank-logos/bri.png',
      'PERMATA': '/bank-logos/permata.png',
      'QRIS': '/bank-logos/qris.png',
      'OVO': '/bank-logos/ovo.png',
      'DANA': '/bank-logos/dana.png',
      'SHOPEEPAY': '/bank-logos/shopeepay.png',
      // Duitku 2-char channel codes
      'SP': '/bank-logos/qris.png',   // ShopeePay QRIS
      'SQ': '/bank-logos/qris.png',   // NusaPay QRIS
      'NQ': '/bank-logos/qris.png',   // NusaPay QRIS (alt)
      'SA': '/bank-logos/shopeepay.png', // ShopeePay App
      'OV': '/bank-logos/ovo.png',
      'DA': '/bank-logos/dana.png',
      'BR': '/bank-logos/bri.png',
      'BC': '/bank-logos/bca.png',
      'M2': '/bank-logos/mandiri.png',
      'BT': '/bank-logos/permata.png',
    };
    return logos[method?.toUpperCase()] || null;
  };

  const getBankName = (method) => {
    const names = {
      // Xendit / friendly names
      'MANDIRI': 'Bank Mandiri',
      'BCA': 'Bank BCA',
      'BNI': 'Bank BNI',
      'BRI': 'Bank BRI',
      'PERMATA': 'Bank Permata',
      'BSI': 'Bank Syariah Indonesia',
      'CIMB': 'Bank CIMB Niaga',
      'BJB': 'Bank BJB',
      'QRIS': 'QRIS',
      'OVO': 'OVO',
      'DANA': 'DANA',
      'SHOPEEPAY': 'ShopeePay',
      'ALFAMART': 'Alfamart',
      'FIXED_VA': 'Virtual Account',
      // Duitku 2-char channel codes -> friendly Indonesian names
      'SP': 'ShopeePay QRIS',
      'SQ': 'NusaPay QRIS',
      'NQ': 'NusaPay QRIS',
      'SA': 'ShopeePay App',
      'OV': 'OVO',
      'DA': 'DANA',
      'LA': 'LinkAja',
      'LQ': 'LinkAja QRIS',
      'LF': 'LinkAja Apps',
      'VA': 'Maybank VA',
      'BR': 'BRI VA',
      'BC': 'BCA VA',
      'BT': 'Permata VA',
      'B1': 'CIMB Niaga VA',
      'BN': 'BNI VA',
      'A1': 'ATM Bersama',
      'AG': 'Bank Artha Graha VA',
      'BV': 'BNC VA',
      'BS': 'BSI VA',
      'DM': 'Danamon VA',
      'FT': 'Alfamart / Retail',
      'IR': 'Indomaret / Retail',
      'M2': 'Mandiri VA',
      'NC': 'BNC VA',
      'SL': 'Sampoerna VA',
      'VC': 'Kartu Kredit Visa/Mastercard',
    };
    return names[method?.toUpperCase()] || method || 'Virtual Account';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md dark:bg-slate-800">
          <p className="text-gray-600 mb-4 dark:text-slate-300">Data pembayaran tidak ditemukan</p>
          <Button onClick={handleClose}>Kembali</Button>
        </div>
      </div>
    );
  }

  const { customer, invoice, payment, company } = paymentData;
  const isPaid = invoice?.is_paid || payment?.status === 'success' || payment?.status === 'PAID';
  const vaNumber = payment?.va_number || payment?.payment_code || payment?.account_number || invoice?.xendit_va_number || '-';
  const vaName = payment?.va_name || invoice?.xendit_va_name || '';
  const paymentMethod = payment?.va_bank || payment?.payment_channel || payment?.payment_method || invoice?.xendit_va_bank || 'VA';
  const expiryDate = payment?.expiry_date || invoice?.xendit_expiry_date || invoice?.xendit_va_expiry;
  
  // Check if VA needs to be created OR user wants to change method
  const needsVACreation = !isPaid && (vaNumber === '-' || !vaNumber || showMethodSelection);
  const hasExistingPayment = vaNumber && vaNumber !== '-';

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 py-4 px-3">
      <div className="max-w-md mx-auto">
        {/* Action Buttons */}
        <div className="flex justify-center gap-2 mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-white hover:bg-gray-50 text-xs px-2 py-1 h-7 dark:bg-slate-800 dark:hover:bg-slate-700/50"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <span className="text-gray-400 flex items-center text-xs">|</span>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleClose}
            className="text-xs px-2 py-1 h-7"
          >
            <X className="w-3 h-3 mr-1" />
            Tutup
          </Button>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden dark:bg-slate-800">
          {/* Header with Company Logo */}
          <div className="bg-gradient-to-r from-orange-500 to-yellow-500 p-3 text-center">
            {company?.logo_url ? (
              <img 
                src={company.logo_url} 
                alt={company.name} 
                className="h-10 mx-auto mb-1"
              />
            ) : (
              <h1 className="text-lg font-bold text-white">
                {company?.name || 'RADIUSLINK'}
              </h1>
            )}
            <p className="text-orange-100 text-[10px]">HIGH SPEED INTERNET UNLIMITED</p>
          </div>

          {/* Customer Info */}
          <div className="p-3 border-b bg-orange-50 dark:bg-orange-900/30">
            <div className="flex items-start gap-2">
              <div className="bg-white rounded-full p-1.5 shadow dark:bg-slate-800">
                <Clock className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-slate-200">
                  Hai {customer?.name?.toUpperCase()}, ({customer?.account_number})
                </h2>
                <p className="text-gray-600 text-xs mt-0.5 dark:text-slate-300">
                  Bayar sebelum <span className="text-blue-600 font-medium dark:text-blue-400">{formatDate(expiryDate)}</span> dengan nominal yang tepat
                </p>
              </div>
            </div>
          </div>

          {/* Payment Method Selection - Show if payment not created yet OR user wants to change */}
          {needsVACreation && (
            <div className="p-4 border-b">
              <div className="text-center mb-3">
                <CreditCard className="w-8 h-8 text-orange-500 mx-auto mb-1" />
                <h3 className="text-base font-bold text-gray-800 dark:text-slate-200">
                  {showMethodSelection ? 'Ganti Metode Pembayaran' : 'Pilih Metode Pembayaran'}
                </h3>
                <p className="text-xs text-gray-600 dark:text-slate-300">
                  {showMethodSelection 
                    ? 'Pilih metode pembayaran baru (kode sebelumnya akan diganti)'
                    : 'Pilih metode pembayaran yang Anda inginkan'
                  }
                </p>
              </div>
              
              {/* Gateway Selection Tabs */}
              <div className="flex mb-4 bg-gray-100 rounded-lg p-1 dark:bg-slate-800" data-testid="gateway-tabs">
                <button
                  onClick={() => setSelectedGateway('xendit')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    selectedGateway === 'xendit' 
                      ? 'bg-white shadow text-orange-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="xendit-tab"
                >
                  <div className="flex items-center justify-center gap-1">
                    <CreditCard className="w-4 h-4" />
                    <span>Xendit</span>
                  </div>
                </button>
                <button
                  onClick={() => setSelectedGateway('duitku')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    selectedGateway === 'duitku' 
                      ? 'bg-white shadow text-green-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="duitku-tab"
                >
                  <div className="flex items-center justify-center gap-1">
                    <Wallet className="w-4 h-4" />
                    <span>Duitku</span>
                  </div>
                </button>
              </div>
              
              {/* Cancel change button if in change mode */}
              {showMethodSelection && hasExistingPayment && (
                <div className="mb-3 text-center">
                  <button 
                    onClick={() => setShowMethodSelection(false)}
                    className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                  >
                    ← Kembali ke pembayaran sebelumnya
                  </button>
                </div>
              )}
              
              {/* Xendit Payment Options */}
              {selectedGateway === 'xendit' && (
              <>
              {/* Virtual Account Banks */}
              <div className="mb-3">
                <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1 dark:text-slate-200">
                  🏦 Virtual Account
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {vaBanks.map((bank) => (
                    <button
                      key={bank.code}
                      onClick={() => handleDirectPayment(bank.code, 'va')}
                      disabled={creatingPayment}
                      className={`p-2.5 rounded-lg border-2 transition-all flex items-center gap-2 hover:border-orange-400 hover:bg-orange-50 ${
                        creatingPayment && selectedMethod === bank.code
                          ? 'border-orange-500 bg-orange-50 opacity-75'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="w-10 h-7 bg-white rounded flex items-center justify-center overflow-hidden flex-shrink-0 dark:bg-slate-800">
                        {creatingPayment && selectedMethod === bank.code ? (
                          <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <img 
                            src={bank.logo} 
                            alt={bank.name}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-slate-200">{bank.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Other Payment Methods (QRIS & Alfamart) */}
              <div className="mb-3">
                <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1 dark:text-slate-200">
                  💳 Metode Lainnya
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {otherMethods.map((method) => (
                    <button
                      key={method.code}
                      onClick={() => handleDirectPayment(method.code, method.type)}
                      disabled={creatingPayment}
                      className={`p-2.5 rounded-lg border-2 transition-all flex items-center gap-2 hover:border-orange-400 hover:bg-orange-50 ${
                        creatingPayment && selectedMethod === method.code
                          ? 'border-orange-500 bg-orange-50 opacity-75'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="w-10 h-7 bg-white rounded flex items-center justify-center overflow-hidden flex-shrink-0 dark:bg-slate-800">
                        {creatingPayment && selectedMethod === method.code ? (
                          <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <img 
                            src={method.logo} 
                            alt={method.name}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-slate-200">{method.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              </>
              )}

              {/* Duitku Payment Options */}
              {selectedGateway === 'duitku' && (
                <div className="text-center">
                  <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/30">
                    <img 
                      src="https://www.duitku.com/wp-content/uploads/2020/06/logo-duitku.png" 
                      alt="Duitku" 
                      className="h-10 mx-auto mb-2"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <p className="text-sm text-gray-600 mb-3 dark:text-slate-300">
                      Bayar menggunakan berbagai metode pembayaran melalui Duitku
                    </p>
                    <ul className="text-xs text-gray-500 mb-4 space-y-1 dark:text-slate-400">
                      <li>✓ Virtual Account (Semua Bank)</li>
                      <li>✓ QRIS</li>
                      <li>✓ E-Wallet (OVO, DANA, LinkAja)</li>
                      <li>✓ Minimarket (Alfamart, Indomaret)</li>
                    </ul>
                  </div>
                  <Button
                    onClick={handleDuitkuPayment}
                    disabled={creatingPayment}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
                    data-testid="duitku-pay-button"
                  >
                    {creatingPayment ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Memproses...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-center">
                        <Wallet className="w-5 h-5" />
                        <span>Bayar dengan Duitku</span>
                      </div>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Payment Details - Show if payment already created */}
          {!needsVACreation && (
          <div className="p-3">
            {/* Check if this is Retail payment (Alfamart/Indomaret) */}
            {['ALFAMART'].includes(paymentMethod?.toUpperCase()) ? (
              // Retail Payment Layout (Alfamart/Indomaret)
              <div>
                {/* Retail Logo */}
                <div className="text-center mb-3">
                  <img 
                    src={paymentMethod?.toUpperCase() === 'ALFAMART' 
                      ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/ALFAMART_LOGO_BARU.png/320px-ALFAMART_LOGO_BARU.png'
                      : 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9a/Logo_Indomaret.svg/320px-Logo_Indomaret.svg.png'
                    }
                    alt={paymentMethod}
                    className="h-10 mx-auto"
                  />
                  <p className="text-xs text-gray-500 mt-1 italic dark:text-slate-400">Belanja puas, bayar pas!</p>
                </div>

                {/* Payment Info Table */}
                <div className="border-t-2 border-red-500 rounded-lg overflow-hidden text-sm">
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Kode Bayar</td>
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-bold text-red-600 tracking-wide dark:text-red-400">
                              {vaNumber}
                            </span>
                            <button 
                              onClick={() => copyToClipboard(vaNumber)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                            >
                              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Total</td>
                        <td className="py-2 px-2">
                          <span className="text-base font-bold text-orange-600 dark:text-orange-400">
                            Rp. {formatCurrency(invoice?.total || payment?.amount || 0)}
                          </span>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Invoice</td>
                        <td className="py-2 px-2">
                          <span className="font-medium text-gray-800 text-xs dark:text-slate-200">
                            {invoice?.invoice_number || '-'}
                          </span>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Paket</td>
                        <td className="py-2 px-2">
                          <span className="font-medium text-gray-800 text-xs dark:text-slate-200">
                            {customer?.service_plan || '-'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Status</td>
                        <td className="py-2 px-2">
                          <span className={`font-bold text-sm ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                            {isPaid ? 'LUNAS' : 'BELUM BAYAR'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Retail Note */}
                <p className="text-orange-600 text-[10px] mt-2 italic dark:text-orange-400">
                  *** Kode Bayar bersifat TETAP dan bisa digunakan lagi untuk pembayaran selanjutnya
                </p>

                {/* Retail Instructions Button */}
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/30">
                  <h4 className="text-xs font-bold text-blue-800 mb-2">📋 Cara Pembayaran di Alfamart:</h4>
                  <ol className="text-[10px] text-blue-700 space-y-1 list-decimal list-inside dark:text-blue-300">
                    <li>Kunjungi Alfamart/Alfamidi terdekat</li>
                    <li>Beritahu kasir bayar ke <strong>{company?.name || 'ISP'}</strong></li>
                    <li>Tunjukkan kode bayar: <span className="font-bold">{vaNumber}</span></li>
                    <li>Bayar sesuai jumlah tagihan</li>
                    <li>Simpan struk sebagai bukti</li>
                    <li>Internet otomatis aktif setelah pembayaran</li>
                  </ol>
                </div>
              </div>
            ) : paymentMethod?.toUpperCase() === 'QRIS' ? (
              // QRIS Payment Layout
              <div className="text-center">
                <div className="mb-3">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 dark:bg-purple-900/40">
                    <span className="text-3xl">📱</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-700 dark:text-slate-200">QRIS</h3>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Scan & Pay</p>
                </div>
                
                {/* QR Code would be here if available */}
                <div className="bg-gray-100 p-4 rounded-lg mb-3 dark:bg-slate-800">
                  <p className="text-sm text-gray-600 dark:text-slate-300">
                    Silakan scan QR Code di halaman pembayaran Xendit
                  </p>
                </div>

                <div className="border-t-2 border-purple-500 rounded-lg overflow-hidden text-sm">
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Total</td>
                        <td className="py-2 px-2">
                          <span className="text-base font-bold text-orange-600 dark:text-orange-400">
                            Rp. {formatCurrency(invoice?.total || payment?.amount || 0)}
                          </span>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Invoice</td>
                        <td className="py-2 px-2 text-xs font-medium">{invoice?.invoice_number || '-'}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Status</td>
                        <td className="py-2 px-2">
                          <span className={`font-bold text-sm ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                            {isPaid ? 'LUNAS' : 'BELUM BAYAR'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              // Virtual Account Layout (Default)
              <>
            {/* Bank/Payment Method Logo */}
            <div className="text-center mb-3">
              {getPaymentMethodLogo(paymentMethod) ? (
                <img 
                  src={getPaymentMethodLogo(paymentMethod)} 
                  alt={paymentMethod}
                  className="h-8 mx-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <h3 className="text-base font-bold text-gray-700 mt-1 dark:text-slate-200">
                {getBankName(paymentMethod)}
              </h3>
              {vaName && (
                <p className="text-xs text-gray-500 dark:text-slate-400">{vaName}</p>
              )}
            </div>

            {/* Payment Info Table - Compact */}
            <div className="border-t-2 border-teal-500 rounded-lg overflow-hidden text-sm">
              <table className="w-full">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">No VA</td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-blue-600 tracking-wide dark:text-blue-400">
                          {vaNumber.replace(/(\d{4})/g, '$1 ').trim()}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(vaNumber)}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {vaName && (
                  <tr className="border-b">
                    <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Nama</td>
                    <td className="py-2 px-2">
                      <span className="font-medium text-gray-800 text-xs dark:text-slate-200">{vaName}</span>
                    </td>
                  </tr>
                  )}
                  <tr className="border-b">
                    <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Total</td>
                    <td className="py-2 px-2">
                      <span className="text-base font-bold text-orange-600 dark:text-orange-400">
                        Rp. {formatCurrency(invoice?.total || payment?.amount || 0)}
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Invoice</td>
                    <td className="py-2 px-2">
                      <span className="font-medium text-gray-800 text-xs dark:text-slate-200">
                        {invoice?.invoice_number || '-'}
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Paket</td>
                    <td className="py-2 px-2">
                      <span className="font-medium text-gray-800 text-xs dark:text-slate-200">
                        {customer?.service_plan || '-'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 text-gray-600 bg-gray-50 text-xs dark:text-slate-300 dark:bg-slate-900">Status</td>
                    <td className="py-2 px-2">
                      <span className={`font-bold text-sm ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                        {isPaid ? 'LUNAS' : 'BELUM BAYAR'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* VA Note */}
            {vaNumber !== '-' && (
              <p className="text-orange-600 text-[10px] mt-2 italic dark:text-orange-400">
                *** No VA bersifat TETAP untuk pembayaran selanjutnya
              </p>
            )}
              </>
            )}
            
            {/* Change Payment Method Button */}
            {!isPaid && (
              <div className="mt-3 text-center">
                <button 
                  onClick={() => setShowMethodSelection(true)}
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1 mx-auto dark:text-blue-400"
                >
                  <RefreshCw className="w-3 h-3" />
                  Ganti Metode Pembayaran
                </button>
              </div>
            )}
          </div>
          )}

          {/* Instructions - Only for VA */}
          {!needsVACreation && !['ALFAMART', 'QRIS'].includes(paymentMethod?.toUpperCase()) && (
          <div className="bg-gray-50 p-3 text-center border-t dark:bg-slate-900">
            <p className="text-gray-700 text-xs dark:text-slate-200">
              Bayar ke nomor VA diatas. Internet otomatis aktif setelah pembayaran terkonfirmasi.
            </p>
            <p className="text-gray-600 text-[10px] mt-1 dark:text-slate-300">
              Salam Hormat, <span className="font-semibold">{company?.name || 'RADIUSLINK'}</span>
            </p>
          </div>
          )}

          {/* Pay Now Button (if has payment URL) */}
          {(payment?.payment_url || redirectUrl) && !isPaid && !needsVACreation && (
            <div className="p-3 bg-white border-t dark:bg-slate-800">
              <Button 
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white py-2 text-sm"
                onClick={() => window.location.href = redirectUrl || payment.payment_url}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Bayar Sekarang
              </Button>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-100 py-2 text-center border-t dark:bg-slate-800">
            <p className="text-[10px] text-gray-600 dark:text-slate-300">
              2025 © {company?.name || 'RADIUSLINK'} - Powered By{' '}
              <span className="text-blue-600 font-semibold dark:text-blue-400">XENDIT</span> & <span className="text-green-600 font-semibold dark:text-green-400">DUITKU</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;
